import React, { useEffect, useState } from 'react';
import { SearchIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Footer } from './components/Footer';
import { Modal } from './components/Modal';
import { PrivacyPolicyContent, ContactUsContent, TermsOfUseContent } from './components/ModalContent';
export function App() {
  const navigate = useNavigate();
  const [modalType, setModalType] = useState<'privacy' | 'contact' | 'terms' | null>(null);
  useEffect(() => {
    // Check for URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    // Save cid parameter
    const cid = urlParams.get('cid');
    if (cid) {
      localStorage.setItem('bemobclick', cid);
    }
    // Save additional parameters
    const ts = urlParams.get('ts');
    if (ts) {
      localStorage.setItem('ts', ts);
    }
    const targetingdomainid = urlParams.get('targetingdomainid');
    if (targetingdomainid) {
      localStorage.setItem('targetingdomainid', targetingdomainid);
    }
    const keywordid = urlParams.get('keywordid');
    if (keywordid) {
      localStorage.setItem('keywordid', keywordid);
    }
  }, []);
  const handleOpenModal = (type: 'privacy' | 'contact' | 'terms') => {
    setModalType(type);
  };
  const handleCloseModal = () => {
    setModalType(null);
  };
  const handleStartQuiz = () => {
    navigate('/quiz');
  };
  const getModalContent = () => {
    switch (modalType) {
      case 'privacy':
        return {
          title: 'Privacy Policy',
          content: <PrivacyPolicyContent data-id="element-0" />
        };
      case 'contact':
        return {
          title: 'Contact Us',
          content: <ContactUsContent data-id="element-1" />
        };
      case 'terms':
        return {
          title: 'Terms of Use',
          content: <TermsOfUseContent data-id="element-2" />
        };
      default:
        return {
          title: '',
          content: null
        };
    }
  };
  const {
    title,
    content
  } = getModalContent();
  return <div className="w-full min-h-screen font-['Open_Sans']" data-id="element-3">
      {/* Import Google Fonts */}
      <style data-id="element-4">
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Open+Sans:wght@400;600&display=swap');
          .hero-gradient {
            background: linear-gradient(135deg, #0039a6 0%, #6a11cb 100%);
          }
          .cta-button {
            transition: all 0.3s ease;
          }
          .cta-button:hover {
            box-shadow: 0 0 15px rgba(255, 255, 255, 0.6);
            transform: scale(1.03);
          }
          .pulse-animation {
            animation: pulse 3s infinite;
          }
          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(0, 200, 81, 0.7);
            }
            50% {
              box-shadow: 0 0 0 10px rgba(0, 200, 81, 0);
            }
          }
        `}
      </style>
      {/* SECTION 1: HERO / HEADER */}
      <section className="hero-gradient flex flex-col items-center justify-center px-4 py-2 relative overflow-hidden pb-20" data-id="element-5">
        {/* Logo */}
        <div className="mb-12" data-id="element-6">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TqMEYVHuTPuBcgwmU1HgU/FINANCE_TIME_%282%29.png" alt="Finance-Time" className="h-8 md:h-8" data-id="element-7" />
        </div>
        {/* Content */}
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-8">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Montserrat'] font-extrabold text-white mb-6 leading-tight" data-id="element-9">
            See Your Credit Score Instantly – and Discover How to Raise It FAST!
          </h1>
          <p className="text-lg md:text-xl text-white mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-10">
            Most Americans have no idea what's secretly dragging down their
            credit.
            <br className="hidden md:block" data-id="element-11" />
            Find out your real score now and learn how to boost it —{' '}
            <strong data-id="element-12">completely free.</strong>
          </p>
          <button onClick={handleStartQuiz} className="cta-button inline-flex items-center gap-3 bg-[#00C851] text-white text-lg md:text-xl font-semibold px-10 py-5 rounded-xl hover:bg-[#00b347] shadow-lg" data-id="element-13">
            <SearchIcon className="w-6 h-6" data-id="element-14" />
            Get My Free Credit Score Now
          </button>
        </div>
      </section>
      {/* SECTION 2: MOTIVATION / BENEFITS */}
      <section className="bg-white py-10 px-4" data-id="element-15">
        <div className="max-w-3xl mx-auto text-center" data-id="element-16">
          <h2 className="text-2xl md:text-3xl font-['Montserrat'] font-bold text-[#2E2E2E] mb-8" data-id="element-17">
            You could be losing thousands because of a bad credit score — and
            not even know it.
          </h2>
          <div className="space-y-6 mb-10 text-left md:text-center max-w-2xl mx-auto" data-id="element-18">
            <div className="flex items-start md:items-center gap-4" data-id="element-19">
              <AlertTriangleIcon className="w-6 h-6 text-[#00C851] flex-shrink-0 mt-1 md:mt-0" data-id="element-20" />
              <p className="text-lg text-[#2E2E2E]" data-id="element-21">
                Higher rates on car loans and credit cards.
              </p>
            </div>
            <div className="flex items-start md:items-center gap-4" data-id="element-22">
              <AlertTriangleIcon className="w-6 h-6 text-[#00C851] flex-shrink-0 mt-1 md:mt-0" data-id="element-23" />
              <p className="text-lg text-[#2E2E2E]" data-id="element-24">
                Getting declined for personal loans or apartments.
              </p>
            </div>
            <div className="flex items-start md:items-center gap-4" data-id="element-25">
              <AlertTriangleIcon className="w-6 h-6 text-[#00C851] flex-shrink-0 mt-1 md:mt-0" data-id="element-26" />
              <p className="text-lg text-[#2E2E2E]" data-id="element-27">
                Even employers may check your credit.
              </p>
            </div>
          </div>
          <p className="text-xl text-[#2E2E2E] mb-8 font-semibold" data-id="element-28">
            👉 Start improving your score today — it's completely free.
          </p>
          <button onClick={handleStartQuiz} className="cta-button inline-flex items-center gap-3 bg-[#00C851] text-white text-lg md:text-xl font-semibold px-10 py-5 rounded-xl hover:bg-[#00b347] shadow-lg" data-id="element-29">
            <CheckCircleIcon className="w-6 h-6" data-id="element-30" />
            Check My Free Credit Score
          </button>
        </div>
      </section>
      {/* SECTION 3: FINAL CTA / FOOTER */}
      <section id="leadform" className="bg-[#002E6D] py-20 px-4" data-id="element-31">
        <div className="max-w-3xl mx-auto text-center" data-id="element-32">
          <h2 className="text-2xl md:text-3xl font-['Montserrat'] font-bold text-white mb-10" data-id="element-33">
            Your credit changes every month. Don't wait until it's too late to
            fix it.
          </h2>
          <button onClick={handleStartQuiz} className="pulse-animation cta-button inline-flex items-center gap-3 bg-[#00C851] text-white text-lg md:text-xl font-semibold px-10 py-5 rounded-xl hover:bg-[#00b347] shadow-lg mb-8" data-id="element-34">
            🚀 Get My Free Credit Score & Start Improving Today
          </button>
          <p className="text-sm text-white/70 max-w-2xl mx-auto" data-id="element-35">
            By checking your score, you agree to our Privacy Policy. No credit
            card required.
          </p>
        </div>
      </section>
      {/* Footer */}
      <Footer onOpenModal={handleOpenModal} data-id="element-36" />
      {/* Modal */}
      <Modal isOpen={modalType !== null} onClose={handleCloseModal} title={title} data-id="element-37">
        {content}
      </Modal>
    </div>;
}